import {HourlyWeatherForecastItem} from '../../api/types';

/**
 * Where the current temperature sits inside today's range, and how much of that
 * range the day has actually covered so far.
 *
 * All four positional fields are fractions of the day's span, 0 at `min` and 1
 * at `max`, so a view can use them as percentages without knowing the units.
 */
export type DayRange = {
  /** Coldest and warmest hour of the whole civil day, past *and* future. */
  min: number;
  max: number;
  /**
   * When each extreme falls. On a tie the first row in `today` wins, which is
   * the earliest occurrence only if the caller passes the day in chronological
   * order -- the API does, and nothing here re-sorts it.
   */
  minAt: number;
  maxAt: number;
  /**
   * Whether each extreme is behind us. This is the whole point of the type:
   * at 14:00 a 06:00 minimum is a fact and a 15:00 maximum is still a forecast,
   * and the card says so ("12 deg at 06:00" vs "21 deg expected 15:00").
   */
  minReached: boolean;
  maxReached: boolean;
  /**
   * The part of the day's range that has *occurred* — coldest-so-far to
   * warmest-so-far. Not anchored at 0: overnight, before either extreme has
   * been reached, this is a short segment floating between the two ends. A
   * left-anchored fill would claim the day's minimum was already behind you at
   * 03:00.
   */
  bandFrom: number;
  bandTo: number;
  /** Current temperature, clamped into 0..1. */
  position: number;
  /**
   * The day has no spread at all (fog, unbroken overcast), so `max - min` is 0
   * and every fraction above would be 0/0. Views must render a plain "steady
   * all day" line rather than a bar: a NaN reaching a style lays the bar out at
   * `left: NaN`.
   */
  isFlat: boolean;
};

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * `today` is the civil day's hourly rows — already filtered by the caller, so
 * this stays free of timezone logic and is exhaustively testable. `current` is
 * the row the card is displaying, which is *not* guaranteed to be one of them:
 * it is chosen by a reduce that can fall outside today, which is why
 * `position` is clamped rather than trusted.
 */
export const dayRange = (
  today: HourlyWeatherForecastItem[],
  current: HourlyWeatherForecastItem,
): DayRange | null => {
  if (today.length === 0) {
    return null;
  }

  let minItem = today[0];
  let maxItem = today[0];
  for (const item of today) {
    if (item.T < minItem.T) {
      minItem = item;
    }
    if (item.T > maxItem.T) {
      maxItem = item;
    }
  }

  const min = minItem.T;
  const max = maxItem.T;
  const minReached = minItem.timestamp <= current.timestamp;
  const maxReached = maxItem.timestamp <= current.timestamp;
  const span = max - min;

  if (span === 0) {
    return {
      min,
      max,
      minAt: minItem.timestamp,
      maxAt: maxItem.timestamp,
      minReached,
      maxReached,
      bandFrom: 0,
      bandTo: 1,
      position: 0,
      isFlat: true,
    };
  }

  // Empty when `current` predates every row of today; fall back to the reading
  // itself so the band collapses to a point. Without it the seeded loop below
  // would throw on `soFar[0].T`.
  const elapsed = today.filter((item) => item.timestamp <= current.timestamp);
  const soFar = elapsed.length > 0 ? elapsed : [current];
  const scale = (t: number) => clamp01((t - min) / span);

  let minSoFar = soFar[0].T;
  let maxSoFar = soFar[0].T;
  for (const item of soFar) {
    if (item.T < minSoFar) {
      minSoFar = item.T;
    }
    if (item.T > maxSoFar) {
      maxSoFar = item.T;
    }
  }

  return {
    min,
    max,
    minAt: minItem.timestamp,
    maxAt: maxItem.timestamp,
    minReached,
    maxReached,
    bandFrom: scale(minSoFar),
    bandTo: scale(maxSoFar),
    position: scale(current.T),
    isFlat: false,
  };
};
